import { AccountsApiError, apiFetchAccounts } from './apiAccounts';

export type ApiDiscussionVoteValue = 'up' | 'down' | null;

export interface ApiDiscussionAuthor {
  display_name: string;
}

export interface ApiDiscussionVotes {
  up_count: number;
  down_count: number;
  score: number;
  current_user_vote: 'up' | 'down' | null;
}

export interface ApiDiscussionComment {
  id: string;
  reference: string;
  parent_id?: string | null;
  body: string;
  status: 'visible' | 'deleted' | 'hidden';
  created_at: string;
  updated_at: string;
  author: ApiDiscussionAuthor;
  votes: ApiDiscussionVotes;
  can_edit: boolean;
  can_delete: boolean;
}

export interface ApiDiscussionThreadResponse {
  reference_no: string;
  comment_count: number;
  thread_count: number;
  has_more?: boolean;
  next_before?: string | null;
  moderation?: ApiDiscussionModerationResult;
  comments: ApiDiscussionComment[];
}

export interface ApiDiscussionModerationResult {
  comment_id: string;
  status: 'visible' | 'deleted' | 'hidden';
  level: 'clear' | 'held';
  reasons?: string[];
  message?: string;
}

export interface ApiDiscussionPreviewComment {
  id: string;
  body: string;
  status: 'visible' | 'deleted' | 'hidden';
  created_at: string;
  reply_count: number;
  author: ApiDiscussionAuthor;
}

export interface ApiDiscussionSummary {
  reference_no: string;
  comment_count: number;
  thread_count: number;
  preview_comments: ApiDiscussionPreviewComment[];
}

export interface ApiDiscussionSummariesResponse {
  summaries: ApiDiscussionSummary[];
  partial?: boolean;
}

export interface ApiDiscussionThreadFetchOptions {
  limit?: number;
  before?: string | null;
  council?: string | null;
}

function normalizeDiscussionReference(reference: string): string {
  return String(reference || '').replace(/\s+/g, '').trim().toUpperCase();
}

function normalizeDiscussionsPath(pathname: string): string {
  let path = (pathname || '').trim().replace(/\/+$/, '');
  if (!path) path = '/';

  const lower = path.toLowerCase();
  if (lower.endsWith('/api/v1/accounts')) {
    return path.slice(0, -'/api/v1/accounts'.length) + '/api/v1/discussions';
  }
  if (lower.endsWith('/accounts')) {
    return path.slice(0, -'/accounts'.length) + '/discussions';
  }
  if (path === '/') return '/api/v1/discussions';
  if (lower.endsWith('/api')) return path + '/v1/discussions';
  if (lower.endsWith('/api/v1')) return path + '/discussions';
  return path;
}

function getDiscussionsApiBaseUrl(): string {
  // Public discussion reads go through the same-origin proxy in the browser.
  // This avoids CORS errors while preserving retryable HTTP failures and
  // caching only complete summary responses.
  if (typeof window !== 'undefined') {
    return '/api/discussions';
  }

  const rawBaseUrl = process.env.NEXT_PUBLIC_API_URL?.trim();
  if (!rawBaseUrl) {
    throw new AccountsApiError('Missing NEXT_PUBLIC_API_URL', 0);
  }

  if (rawBaseUrl.startsWith('/')) {
    return normalizeDiscussionsPath(rawBaseUrl);
  }

  try {
    const url = new URL(rawBaseUrl);
    return url.origin + normalizeDiscussionsPath(url.pathname);
  } catch {
    throw new AccountsApiError('Invalid NEXT_PUBLIC_API_URL', 0);
  }
}

async function parseDiscussionsResponse<T>(res: Response): Promise<T> {
  const contentType = res.headers.get('content-type')?.toLowerCase() ?? '';
  const text = await res.text();
  const trimmed = text.trim();

  if (!res.ok) {
    let parsed: unknown = undefined;
    if (trimmed && (contentType.includes('json') || trimmed.startsWith('{') || trimmed.startsWith('['))) {
      try {
        parsed = JSON.parse(trimmed);
      } catch {
        parsed = undefined;
      }
    }
    const parsedObject = parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
    const detail = typeof parsedObject?.detail === 'string' ? parsedObject.detail : undefined;
    throw new AccountsApiError(
      detail ? `discussions API ${res.status}: ${detail}` : `discussions API ${res.status}`,
      res.status,
      parsed
    );
  }

  if (!trimmed.length) {
    return undefined as T;
  }

  if (contentType.includes('json') || trimmed.startsWith('{') || trimmed.startsWith('[')) {
    return JSON.parse(trimmed) as T;
  }

  return trimmed as unknown as T;
}

async function apiFetchPublicDiscussions<T = unknown>(path: string, init: RequestInit = {}): Promise<T> {
  const baseUrl = getDiscussionsApiBaseUrl();
  const headers = new Headers(init.headers as HeadersInit | undefined);
  const hasBody = init.body !== undefined && init.body !== null;

  if (hasBody && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  } else if (!hasBody && headers.has('Content-Type')) {
    headers.delete('Content-Type');
  }

  let res: Response;
  try {
    res = await fetch(baseUrl + path, {
      ...init,
      headers,
    });
  } catch {
    throw new AccountsApiError('Network error', 0);
  }

  return parseDiscussionsResponse<T>(res);
}

export function buildDiscussionReferencePath(
  reference: string,
  options: ApiDiscussionThreadFetchOptions = {}
): string {
  const path = `/${encodeURIComponent(normalizeDiscussionReference(reference))}`;
  const params = new URLSearchParams();

  if (typeof options.limit === 'number' && Number.isFinite(options.limit) && options.limit > 0) {
    params.set('limit', String(Math.floor(options.limit)));
  }
  if (typeof options.before === 'string' && options.before.trim()) {
    params.set('before', options.before.trim());
  }
  if (typeof options.council === 'string' && options.council.trim()) {
    params.set('council', options.council.trim());
  }

  const query = params.toString();
  return query ? `${path}?${query}` : path;
}

export async function fetchDiscussionThreadPublic(
  reference: string,
  options: ApiDiscussionThreadFetchOptions = {}
): Promise<ApiDiscussionThreadResponse> {
  return apiFetchPublicDiscussions<ApiDiscussionThreadResponse>(buildDiscussionReferencePath(reference, options));
}

export async function fetchDiscussionThreadAuthenticated(
  reference: string,
  options: ApiDiscussionThreadFetchOptions = {}
): Promise<ApiDiscussionThreadResponse> {
  return apiFetchAccounts<ApiDiscussionThreadResponse>(`/discussions${buildDiscussionReferencePath(reference, options)}`);
}

export async function fetchDiscussionSummaries(
  references: string[],
  council?: string | null
): Promise<ApiDiscussionSummariesResponse> {
  const params = new URLSearchParams();
  params.set('references', references.join(','));
  if (council) params.set('council', council);
  return apiFetchPublicDiscussions<ApiDiscussionSummariesResponse>(`/summaries?${params.toString()}`);
}

export async function createDiscussionCommentApi(
  reference: string,
  payload: { body: string; parent_id?: string | null; author_display_name?: string; council?: string | null }
): Promise<ApiDiscussionThreadResponse> {
  const { council, ...body } = payload;
  return apiFetchAccounts<ApiDiscussionThreadResponse>(
    `/discussions${buildDiscussionReferencePath(reference, { council })}/comments`,
    {
      method: 'POST',
      body: JSON.stringify(body),
    }
  );
}

export async function updateDiscussionCommentApi(
  commentId: string,
  payload: { body: string }
): Promise<ApiDiscussionThreadResponse> {
  return apiFetchAccounts<ApiDiscussionThreadResponse>(`/discussions/comments/${encodeURIComponent(commentId)}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

export async function deleteDiscussionCommentApi(commentId: string): Promise<ApiDiscussionThreadResponse> {
  return apiFetchAccounts<ApiDiscussionThreadResponse>(`/discussions/comments/${encodeURIComponent(commentId)}`, {
    method: 'DELETE',
  });
}

export async function setDiscussionVoteApi(
  commentId: string,
  value: ApiDiscussionVoteValue
): Promise<ApiDiscussionThreadResponse> {
  return apiFetchAccounts<ApiDiscussionThreadResponse>(`/discussions/comments/${encodeURIComponent(commentId)}/vote`, {
    method: 'PUT',
    body: JSON.stringify({ value }),
  });
}
